import { useState } from "react";

import { css } from "@compiled/react";

import Avatar from "@atlaskit/avatar";
import AvatarGroup from "@atlaskit/avatar-group";
import Link from "@atlaskit/link";
import { token } from "@atlaskit/tokens";

import { NotificationGroup as NotificationGroupType, Actor } from "./types";

const GroupContainer = css({
    marginBlockEnd: token("space.200")
});

const GroupTitle = css({
    fontSize: token("font.body"),
    fontWeight: token("font.weight.bold"),
    color: token("color.text.subtle"),
    marginBlockEnd: token("space.100")
});

const NotificationItem = css({
    "display": "flex",
    "alignItems": "flex-start",
    "flexDirection": "column",
    "padding": token("space.100"),
    "borderRadius": token("border.radius.100"),
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const NotificationItemContent = css({
    display: "flex",
    alignItems: "center",
    width: "100%"
});

const CollapsedGroupItem = css({
    display: "flex",
    alignItems: "center",
    marginBlockStart: token("space.100"),
    paddingInlineStart: token("space.300"),
    width: "100%"
});

const AvatarContainer = css({
    marginInlineEnd: token("space.200")
});

const IconContainer = css({
    marginInlineEnd: token("space.100")
});

const ClickableText = css({
    "cursor": "pointer",
    "color": token("color.link"),
    "border": "none",
    "background": "none",
    "padding": 0,
    "font": "inherit",
    "&:hover": {
        textDecoration: "underline"
    },
    "&:focus": {
        outline: `2px solid ${token("color.border.focused")}`
    }
});

interface NotificationGroupProps {
    title: string;
    notificationGroups: NotificationGroupType[];
}

const NotificationGroup = ({
    title,
    notificationGroups
}: NotificationGroupProps) => {
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

    return (
        <div css={GroupContainer}>
            <h5 css={GroupTitle}>{title}</h5>
            {notificationGroups.map((group) => {
                const firstNotification = group.notifications[0];
                const isExpanded = expandedGroupId === group.id;
                const hasAdditionalActors =
                    group.additionalActors && group.additionalActors.length > 0;

                if (!firstNotification) return null;

                return (
                    <div key={group.id} css={NotificationItem}>
                        {/* Main notification content */}
                        <div css={NotificationItemContent}>
                            <div css={AvatarContainer}>
                                <Avatar
                                    size="medium"
                                    src={
                                        firstNotification.content.actors?.[0]
                                            ?.avatarUrl
                                    }
                                />
                            </div>
                            <div>
                                <p>{firstNotification.content.message}</p>
                                <div
                                    css={css({
                                        display: "flex",
                                        alignItems: "center"
                                    })}
                                >
                                    <div css={IconContainer}>
                                        {/* eslint-disable-next-line no-restricted-syntax */}
                                        <img
                                            src={
                                                firstNotification.content.entity
                                                    ?.icon?.url
                                            }
                                            height={16}
                                            width={16}
                                            alt={
                                                firstNotification.content.entity
                                                    ?.title
                                            }
                                        />
                                    </div>
                                    <Link
                                        href={
                                            firstNotification.content.entity
                                                ?.link?.url
                                        }
                                    >
                                        {
                                            firstNotification.content.entity
                                                ?.title
                                        }
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Collapsed group for additional actors - nested inside the card */}
                        {hasAdditionalActors && (
                            <div css={CollapsedGroupItem}>
                                <div css={AvatarContainer}>
                                    <AvatarGroup
                                        data={group.additionalActors.map(
                                            (actor: Actor) => ({
                                                src: actor.avatarUrl,
                                                name: actor.displayName
                                            })
                                        )}
                                        size="medium"
                                    />
                                </div>
                                <div>
                                    <button
                                        css={ClickableText}
                                        type="button"
                                        onClick={() =>
                                            setExpandedGroupId(
                                                isExpanded ? null : group.id
                                            )
                                        }
                                    >
                                        {`+${group.additionalActors.length} updates from ${group.additionalActors[0]?.displayName} and others`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
export default NotificationGroup;
